/**
 * Virtual Try-On System - IMPROVED MOBILE RESPONSIVE VERSION
 * Features: PD tool fills top section on mobile, video fills remaining space
 * UPDATED: Added mandatory consent gate before camera activation (BIPA/multi-state compliant)
 *
 * DEPLOYMENT INSTRUCTIONS FOR DEVELOPER:
 * 1. Shopify Admin -> Online Store -> Themes -> live theme -> Edit code
 * 2. Open assets/vto.js
 * 3. Replace entire contents with this file, save
 * 4. TEST before considering this done:
 *    - Click VTO button first time -> consent modal should appear, camera should NOT start yet
 *    - Click "Allow Camera & Continue" -> camera should activate normally
 *    - Close and reopen VTO -> modal should NOT reappear (consent remembered)
 *    - In a fresh browser/incognito, click "No Thanks" -> camera should NOT activate
 */

console.log('🚀 VTO System Loading (IMPROVED MOBILE + CONSENT)...');

const VTO_CONSENT_STORAGE_KEY = 'illusionEyewearVtoConsentV1';

function hasVTOConsent() {
  try {
    return localStorage.getItem(VTO_CONSENT_STORAGE_KEY) === 'accepted';
  } catch (e) {
    return false;
  }
}

function recordVTOConsent() {
  try {
    const record = {
      status: 'accepted',
      timestamp: new Date().toISOString(),
      sessionId: 'vto-' + Math.random().toString(36).slice(2, 12)
    };
    localStorage.setItem(VTO_CONSENT_STORAGE_KEY, 'accepted');
    localStorage.setItem(VTO_CONSENT_STORAGE_KEY + 'Record', JSON.stringify(record));
  } catch (e) {
    console.warn('Could not persist VTO consent record:', e);
  }
}

function showVTOConsentModal(onAccept, onDecline) {
  const existing = document.getElementById('vto-consent-modal');
  if (existing) existing.remove();

  const overlay = document.createElement('div');
  overlay.id = 'vto-consent-modal';
  overlay.style.cssText = `
    position: fixed; inset: 0; z-index: 100000000;
    background: rgba(0,0,0,0.6); display: flex; align-items: center; justify-content: center;
    padding: 20px; box-sizing: border-box;
  `;

  overlay.innerHTML = `
    <div style="background:#fff; border-radius:12px; max-width:480px; width:100%; padding:28px; box-shadow:0 10px 40px rgba(0,0,0,0.3); font-family: inherit; max-height: 90vh; overflow-y: auto;">
      <h2 style="margin:0 0 14px 0; font-size:20px;">Before You Try On Eyewear</h2>
      <p style="font-size:14px; line-height:1.5; color:#333;">
        Virtual Try-On uses your device's camera to show a live preview of eyewear on your face.
        Here's how it works: your video is processed entirely on your device &mdash; it is
        <strong>never recorded, saved, or sent</strong> to Illusion Eyewear or anyone else.
        We do not store, retain, or keep any copy of your facial data at any time; it is automatically
        discarded the moment you close this window or your session ends. This feature is used only to
        size and position the eyewear preview &mdash; it is not used for identification, advertising, or
        any other purpose, and it is never sold or shared.
      </p>
      <p style="font-size:13px; color:#666; margin-top:10px;">
        By clicking &ldquo;Allow Camera &amp; Continue,&rdquo; you consent to this one-time, on-device
        processing of your facial geometry to generate the preview described above.
        <a href="/pages/virtual-try-on-privacy-consent" target="_blank" rel="noopener" style="color:#2563eb;">Learn more</a>.
      </p>
      <div style="display:flex; gap:12px; margin-top:20px; flex-wrap: wrap;">
        <button id="vto-consent-accept" style="flex:1; min-width:180px; background:#111827; color:#fff; border:none; border-radius:8px; padding:12px 16px; font-size:14px; font-weight:600; cursor:pointer;">
          Allow Camera &amp; Continue
        </button>
        <button id="vto-consent-decline" style="flex:1; min-width:120px; background:#f3f4f6; color:#111827; border:none; border-radius:8px; padding:12px 16px; font-size:14px; font-weight:600; cursor:pointer;">
          No Thanks
        </button>
      </div>
    </div>
  `;

  document.body.appendChild(overlay);

  document.getElementById('vto-consent-accept').addEventListener('click', () => {
    recordVTOConsent();
    overlay.remove();
    if (onAccept) onAccept();
  });

  document.getElementById('vto-consent-decline').addEventListener('click', () => {
    overlay.remove();
    if (onDecline) onDecline();
  });
}

// Wait for all dependencies to load
function waitForDependencies() {
  return new Promise((resolve, reject) => {
    let attempts = 0;
    const maxAttempts = 100;

    const check = () => {
      attempts++;

      if (window.THREE && window.THREE.GLTFLoader && window.FaceLandmarker && window.FilesetResolver) {
        console.log('✅ All dependencies loaded');
        resolve();
      } else if (attempts >= maxAttempts) {
        console.error('❌ Dependencies status:', {
          THREE: !!window.THREE,
          GLTFLoader: !!(window.THREE && window.THREE.GLTFLoader),
          FaceLandmarker: !!window.FaceLandmarker,
          FilesetResolver: !!window.FilesetResolver
        });
        reject(new Error('Dependencies failed to load'));
      } else {
        setTimeout(check, 100);
      }
    };

    check();
  });
}

// Main VTO Class
class VirtualTryOnSystem {
  constructor() {
    this.faceLandmarker = null;
    this.runningMode = 'IMAGE';
    this.webcamRunning = false;
    this.scene = null;
    this.camera = null;
    this.renderer = null;
    this.model = null;
    this.modelLoaded = false;
    this.cameraInitialized = false;
    this.lastVideoTime = -1;
    this.validationMessageTimeout = null;
    this.isMobile = window.innerWidth <= 768;

    window.addEventListener('resize', () => {
      const wasMobile = this.isMobile;
      this.isMobile = window.innerWidth <= 768;

      if (wasMobile !== this.isMobile && this.webcamRunning) {
        this.adjustMobileLayout();
      }
    });

    console.log('✅ VTO System Initialized', this.isMobile ? '(MOBILE MODE)' : '(DESKTOP MODE)');
  }

  async init() {
    try {
      await waitForDependencies();
      await this.createFaceLandmarker();
      this.initThreeJS();
      this.setupButtons();
      this.injectMobileStyles();

      console.log('✅ VTO System Ready');
    } catch (error) {
      console.error('❌ VTO Init Error:', error);
      const webcamButton = document.getElementById('webcamButton');
      if (webcamButton) {
        webcamButton.disabled = true;
        webcamButton.textContent = '❌ System Error';
      }
    }
  }

  injectMobileStyles() {
    const style = document.createElement('style');
    style.textContent = `
      @media only screen and (max-width: 768px) {
        #vto-overlay {
          display: flex !important;
          flex-direction: column !important;
          padding: 0 !important;
        }
        #pd-container {
          position: fixed !important;
          top: 0 !important;
          left: 0 !important;
          right: 0 !important;
          width: 100% !important;
          max-width: 100% !important;
          border-radius: 0 !important;
          margin: 0 !important;
          transform: none !important;
          z-index: 10000 !important;
          box-shadow: 0 2px 8px rgba(0,0,0,0.2) !important;
        }
        #vto-overlay .vto-video-container {
          position: fixed !important;
          top: var(--pd-height, 320px) !important;
          left: 0 !important;
          right: 0 !important;
          bottom: 0 !important;
          width: 100% !important;
          height: calc(100vh - var(--pd-height, 320px)) !important;
          margin: 0 !important;
          padding: 0 !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          background: #000 !important;
          overflow: hidden !important;
        }
        #webcam,
        #three_canvas,
        #output_canvas {
          position: absolute !important;
          top: 0 !important;
          left: 0 !important;
          width: 100% !important;
          height: 100% !important;
          max-width: 100% !important;
          max-height: 100% !important;
          object-fit: cover !important;
          transform: none !important;
        }
        #vto-validation-message {
          position: fixed !important;
          top: calc(var(--pd-height, 320px) + 10px) !important;
          left: 50% !important;
          transform: translateX(-50%) !important;
          font-size: 14px !important;
          padding: 10px 20px !important;
          max-width: 90% !important;
          z-index: 9999 !important;
        }
        #vto-close-btn {
          position: fixed !important;
          top: 10px !important;
          right: 10px !important;
          width: 44px !important;
          height: 44px !important;
          font-size: 24px !important;
          z-index: 10001 !important;
          background: rgba(239, 68, 68, 0.95) !important;
          border-radius: 50% !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          color: white !important;
          border: 2px solid white !important;
          box-shadow: 0 4px 12px rgba(0,0,0,0.3) !important;
        }
        #vto-loading {
          position: fixed !important;
          top: 50% !important;
          left: 50% !important;
          transform: translate(-50%, -50%) !important;
          margin-top: 0 !important;
        }
      }
      @media only screen and (max-width: 480px) {
        :root {
          --pd-height: 340px;
        }
        #vto-overlay .vto-video-container {
          top: 340px !important;
          height: calc(100vh - 340px) !important;
        }
        #vto-validation-message {
          top: 350px !important;
          font-size: 12px !important;
          padding: 8px 15px !important;
        }
      }
      @media only screen and (max-width: 768px) and (orientation: landscape) {
        :root {
          --pd-height: 200px;
        }
        #vto-overlay .vto-video-container {
          top: 200px !important;
          height: calc(100vh - 200px) !important;
        }
        #vto-validation-message {
          top: 210px !important;
        }
      }
      @media only screen and (min-width: 769px) {
        #pd-container {
          position: fixed !important;
          top: 20px !important;
          left: 20px !important;
          width: 380px !important;
          border-radius: 12px !important;
        }
      }
    `;
    document.head.appendChild(style);
    console.log('✅ Mobile styles injected');
  }

  adjustMobileLayout() {
    if (!this.isMobile) return;

    const pdContainer = document.getElementById('pd-container');
    const videoContainer = document.querySelector('.vto-video-container');

    if (pdContainer && videoContainer) {
      const pdHeight = pdContainer.offsetHeight;
      document.documentElement.style.setProperty('--pd-height', `${pdHeight}px`);
      videoContainer.style.top = `${pdHeight}px`;
      videoContainer.style.height = `calc(100vh - ${pdHeight}px)`;
    }

    if (window.pdTool) {
      window.pdTool.adjustForMobile();
    }
  }

  async createFaceLandmarker() {
    console.log('🤖 Creating Face Landmarker...');

    const filesetResolver = await window.FilesetResolver.forVisionTasks(
      'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.2/wasm'
    );

    this.faceLandmarker = await window.FaceLandmarker.createFromOptions(filesetResolver, {
      baseOptions: {
        modelAssetPath: 'https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task',
        delegate: 'GPU'
      },
      outputFaceBlendshapes: true,
      runningMode: 'IMAGE',
      numFaces: 1
    });

    console.log('✅ Face Landmarker created');
  }

  initThreeJS() {
    console.log('🎨 Initializing Three.js...');

    const canvas = document.getElementById('three_canvas');
    if (!canvas) {
      console.error('❌ Three.js canvas not found');
      return;
    }

    this.scene = new window.THREE.Scene();

    this.renderer = new window.THREE.WebGLRenderer({
      canvas: canvas,
      alpha: true,
      antialias: true
    });
    this.renderer.setClearColor(0x000000, 0);

    this.camera = new window.THREE.OrthographicCamera(-1, 1, 1, -1, 0.1, 100);
    this.camera.position.z = 1;

    const ambientLight = new window.THREE.AmbientLight(0xffffff, 3);
    this.scene.add(ambientLight);

    const directionalLight1 = new window.THREE.DirectionalLight(0xffffff, 2);
    directionalLight1.position.set(0, 0, 5);
    this.scene.add(directionalLight1);

    const directionalLight2 = new window.THREE.DirectionalLight(0xffffff, 2);
    directionalLight2.position.set(0, 0, -5);
    this.scene.add(directionalLight2);

    console.log('✅ Three.js initialized');
  }

  async loadGlassesModel() {
    console.log('📦 Loading glasses model...');

    const vtoDataElement = document.getElementById('vto-product-data');
    let modelUrl = null;

    if (vtoDataElement) {
      try {
        const vtoProduct = JSON.parse(vtoDataElement.textContent);
        modelUrl = vtoProduct.model_url;
        console.log('✅ Extracted URL:', modelUrl);
      } catch (error) {
        console.error('Failed to parse product data:', error);
      }
    }

    if (!modelUrl || modelUrl === '' || modelUrl === 'null') {
      const vtoContainer = document.querySelector('.vto-button-container');
      if (vtoContainer) {
        vtoContainer.innerHTML = `
          <div style="padding: 20px; background: #fff3cd; border: 1px solid #ffc107; border-radius: 8px; margin: 20px 0;">
            <h4 style="margin: 0 0 10px 0; color: #856404;">⚠️ Virtual Try-On Not Available</h4>
            <p style="margin: 0; color: #856404;">This product doesn't have a 3D model configured yet.</p>
          </div>
        `;
      }
      throw new Error('No 3D model configured for this product');
    }

    return new Promise((resolve, reject) => {
      const loader = new window.THREE.GLTFLoader();

      loader.load(
        modelUrl,
        (gltf) => {
          this.model = gltf.scene;
          console.log('✅ Model loaded successfully');

          const box = new window.THREE.Box3().setFromObject(this.model);
          const center = box.getCenter(new window.THREE.Vector3());
          this.model.position.sub(center);

          const modelGroup = new window.THREE.Group();
          modelGroup.add(this.model);
          this.scene.add(modelGroup);
          this.model = modelGroup;

          this.model.visible = true;
          this.model.position.set(0, 0, -1.5);

          const initialScale = 0.01;
          this.model.scale.set(initialScale, initialScale, initialScale);

          const size = box.getSize(new window.THREE.Vector3());
          const modelWidth = size.x;

          console.log('📏 Model dimensions (mm):', {
            width: modelWidth.toFixed(2),
            height: size.y.toFixed(2),
            depth: size.z.toFixed(2)
          });

          const TARGET_WIDTH = 150;
          this.modelScaleAdjustment = TARGET_WIDTH / modelWidth;

          console.log('🎯 Scale adjustment factor:', this.modelScaleAdjustment.toFixed(4));

          this.model.traverse((child) => {
            if (child.isMesh) {
              child.visible = true;
              child.frustumCulled = false;

              if (child.material) {
                child.material.transparent = false;
                child.material.opacity = 1;
                child.material.side = window.THREE.DoubleSide;
                child.material.needsUpdate = true;
              }
            }
          });

          this.modelLoaded = true;
          resolve();
        },
        (progress) => {
          if (progress.total > 0) {
            const percentComplete = (progress.loaded / progress.total) * 100;
            console.log(`Loading: ${percentComplete.toFixed(0)}%`);
          }
        },
        (error) => {
          console.error('❌ Model loading error:', error);
          reject(error);
        }
      );
    });
  }

  setupButtons() {
    const webcamButton = document.getElementById('webcamButton');
    const closeButton = document.getElementById('vto-close-btn');

    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      webcamButton.disabled = true;
      webcamButton.textContent = 'Camera not supported';
      return;
    }

    // CONSENT GATE: check/show consent before ever opening the overlay or touching the camera
    webcamButton.addEventListener('click', () => {
      if (hasVTOConsent()) {
        this.openOverlay();
      } else {
        showVTOConsentModal(
          () => this.openOverlay(),
          () => console.log('VTO consent declined by user')
        );
      }
    });
    closeButton.addEventListener('click', () => this.closeOverlay());

    console.log('✅ Buttons ready (consent-gated)');
  }

  async openOverlay() {
    console.log('🎬 Opening VTO overlay...');

    const overlay = document.getElementById('vto-overlay');
    const loading = document.getElementById('vto-loading');
    const video = document.getElementById('webcam');

    overlay.classList.add('active');

    overlay.style.display = "block";
    overlay.style.position = "fixed";
    overlay.style.inset = "0";
    overlay.style.width = "100vw";
    overlay.style.height = "100vh";
    overlay.style.zIndex = "99999999";
    overlay.style.background = "#000";

    loading.style.display = 'block';

    if (window.pdTool && !window.vtoFromCollection) {
      window.pdTool.show();
    }

    if (this.isMobile) {
      setTimeout(() => this.adjustMobileLayout(), 100);
    }

    try {
      await this.startWebcam();
      loading.style.display = 'none';
      video.style.display = 'block';

      if (this.isMobile) {
        setTimeout(() => this.adjustMobileLayout(), 200);
      }
    } catch (error) {
      console.error('❌ Camera error:', error);
      alert('Could not access camera. Please check permissions.');
      this.closeOverlay();
    }
  }

  closeOverlay() {
    console.log('⏹️ Closing VTO overlay...');

    const overlay = document.getElementById('vto-overlay');
    const video = document.getElementById('webcam');
    const loading = document.getElementById('vto-loading');

    this.stopWebcam();

    overlay.classList.remove('active');
    overlay.style.display = "none";
    video.style.display = 'none';
    loading.style.display = 'block';

    if (window.pdTool) {
      window.pdTool.hide();
    }

    this.clearValidationMessage();
  }

  async startWebcam() {
    console.log('📸 Starting webcam...');

    const video = document.getElementById('webcam');
    const canvas = document.getElementById('three_canvas');
    const outputCanvas = document.getElementById('output_canvas');

    const constraints = {
      video: {
        width: this.isMobile ? { ideal: 1280 } : 1280,
        height: this.isMobile ? { ideal: 720 } : 720,
        facingMode: 'user'
      },
      audio: false
    };

    const stream = await navigator.mediaDevices.getUserMedia(constraints);

    video.srcObject = stream;
    this.webcamRunning = true;

    await new Promise((resolve) => {
      video.addEventListener('loadeddata', resolve, { once: true });
    });

    this.setupCanvases(video, canvas, outputCanvas);
    this.predictWebcam();
    this.animate();

    console.log('✅ Webcam started', this.isMobile ? '(Mobile mode)' : '(Desktop mode)');
  }

  setupCanvases(video, threeCanvas, outputCanvas) {
    const videoWidth = video.videoWidth;
    const videoHeight = video.videoHeight;
    const aspect = videoWidth / videoHeight;

    threeCanvas.width = videoWidth;
    threeCanvas.height = videoHeight;
    outputCanvas.width = videoWidth;
    outputCanvas.height = videoHeight;

    video.style.zIndex = '0';
    threeCanvas.style.zIndex = '1';
    outputCanvas.style.zIndex = '2';

    threeCanvas.style.display = 'block';
    threeCanvas.style.pointerEvents = 'none';
    outputCanvas.style.display = 'block';
    outputCanvas.style.pointerEvents = 'none';

    this.camera = new window.THREE.OrthographicCamera(
      -aspect, aspect, 1, -1, 0.1, 100
    );
    this.camera.position.z = 1;
    this.camera.lookAt(0, 0, 0);

    this.renderer.setSize(videoWidth, videoHeight, false);
    this.cameraInitialized = true;

    console.log('✅ Canvases configured:', videoWidth, 'x', videoHeight);
  }

  stopWebcam() {
    console.log('⏹️ Stopping webcam...');

    const video = document.getElementById('webcam');

    if (video.srcObject) {
      video.srcObject.getTracks().forEach(track => track.stop());
      video.srcObject = null;
    }

    this.webcamRunning = false;
    this.cameraInitialized = false;
  }

  async predictWebcam() {
    if (!this.cameraInitialized || !this.webcamRunning) return;

    const video = document.getElementById('webcam');
    const outputCanvas = document.getElementById('output_canvas');
    const ctx = outputCanvas.getContext('2d');

    if (this.runningMode === 'IMAGE') {
      this.runningMode = 'VIDEO';
      await this.faceLandmarker.setOptions({ runningMode: 'VIDEO' });
    }

    const startTimeMs = performance.now();
    if (this.lastVideoTime !== video.currentTime) {
      this.lastVideoTime = video.currentTime;
      const results = this.faceLandmarker.detectForVideo(video, startTimeMs);

      ctx.clearRect(0, 0, outputCanvas.width, outputCanvas.height);

      if (results && results.faceLandmarks && results.faceLandmarks.length > 0) {
        const landmarks = results.faceLandmarks[0];

        this.positionGlasses(landmarks, video);

        if (window.pdTool) {
          window.pdTool.processFrame(landmarks, video);
          window.pdTool.drawVisualization(ctx, landmarks, video);
        }
      } else {
        if (this.model) {
          this.model.visible = false;
        }
        this.showValidationMessage('No face detected - please face the camera');
      }
    }

    if (this.webcamRunning) {
      requestAnimationFrame(() => this.predictWebcam());
    }
  }

  validateFaceOrientation(landmarks) {
    const validation = {
      isValid: true,
      issues: [],
      message: ''
    };

    const leftEye = landmarks[33];
    const rightEye = landmarks[263];
    const noseTip = landmarks[1];

    const faceCenter = (leftEye.x + rightEye.x) / 2;
    const horizontalDeviation = Math.abs(faceCenter - 0.5);

    if (horizontalDeviation > 0.3) {
      validation.isValid = false;
      validation.issues.push('face_off_center');
    }

    const leftEyeToNose = Math.abs(leftEye.x - noseTip.x);
    const rightEyeToNose = Math.abs(rightEye.x - noseTip.x);
    const eyeAsymmetry = Math.abs(leftEyeToNose - rightEyeToNose);

    if (eyeAsymmetry > 0.12) {
      validation.isValid = false;
      validation.issues.push('head_turned');
    }

    const eyeHeightDiff = Math.abs(leftEye.y - rightEye.y);

    if (eyeHeightDiff > 0.12) {
      validation.isValid = false;
      validation.issues.push('head_tilted');
    }

    if (leftEye.x < 0.05 || rightEye.x > 0.95 ||
        leftEye.y < 0.05 || leftEye.y > 0.95 ||
        rightEye.y < 0.05 || rightEye.y > 0.95) {
      validation.isValid = false;
      validation.issues.push('face_out_of_frame');
    }

    const eyeDistance = Math.sqrt(
      Math.pow(rightEye.x - leftEye.x, 2) +
      Math.pow(rightEye.y - leftEye.y, 2)
    );

    if (eyeDistance < 0.12) {
      validation.isValid = false;
      validation.issues.push('too_far_from_camera');
    }

    if (eyeDistance > 0.55) {
      validation.isValid = false;
      validation.issues.push('too_close_to_camera');
    }

    if (!validation.isValid) {
      if (validation.issues.includes('face_off_center')) {
        validation.message = 'Center your face in the frame';
      } else if (validation.issues.includes('head_turned')) {
        validation.message = 'Face the camera directly';
      } else if (validation.issues.includes('head_tilted')) {
        validation.message = 'Keep your head level';
      } else if (validation.issues.includes('face_out_of_frame')) {
        validation.message = 'Move back to fit your face in frame';
      } else if (validation.issues.includes('too_far_from_camera')) {
        validation.message = 'Move closer to the camera';
      } else if (validation.issues.includes('too_close_to_camera')) {
        validation.message = 'Move back from the camera';
      }
    }

    return validation;
  }

  positionGlasses(landmarks, video) {
    if (!this.model || !this.modelLoaded || landmarks.length < 468) {
      return;
    }

    const validation = this.validateFaceOrientation(landmarks);

    if (!validation.isValid) {
      this.model.visible = false;
      this.showValidationMessage(validation.message);
      return;
    }

    this.model.visible = true;
    this.clearValidationMessage();

    const leftEyeOuter = landmarks[33];
    const rightEyeOuter = landmarks[263];
    const leftEyeInner = landmarks[133];
    const rightEyeInner = landmarks[362];
    const noseBridge = landmarks[168];
    const leftTemple = landmarks[234];
    const rightTemple = landmarks[454];

    const aspect = video.videoWidth / video.videoHeight;

    const SCALE_MULTIPLIER = 2.1;
    const VERTICAL_OFFSET = 0.1;
    const HORIZONTAL_OFFSET = 0.0;
    const DEPTH_OFFSET = -30;
    const BASE_SCALE = 0.01;

    const eyeCenterX = (leftEyeInner.x + rightEyeInner.x) / 2;
    const eyeCenterY = (leftEyeOuter.y + rightEyeOuter.y) / 2;
    const eyeCenterZ = (leftEyeOuter.z + rightEyeOuter.z) / 2;

    const posX = (eyeCenterX - 0.5) * 2 * aspect + HORIZONTAL_OFFSET;
    const posY = (0.5 - eyeCenterY) * 2 + VERTICAL_OFFSET;
    const posZ = eyeCenterZ * -10 + DEPTH_OFFSET;

    this.model.position.set(posX, posY, posZ);

    const eyeDistanceX = Math.abs(rightEyeOuter.x - leftEyeOuter.x);
    const eyeDistanceY = Math.abs(rightEyeOuter.y - leftEyeOuter.y);
    const eyeDistance = Math.sqrt(eyeDistanceX * eyeDistanceX + eyeDistanceY * eyeDistanceY);

    const scale = eyeDistance * aspect * SCALE_MULTIPLIER * BASE_SCALE * (this.modelScaleAdjustment || 1);
    this.model.scale.set(scale, scale, scale);

    const rollAngle = Math.atan2(
      rightEyeOuter.y - leftEyeOuter.y,
      rightEyeOuter.x - leftEyeOuter.x
    );

    const avgTempleZ = (leftTemple.z + rightTemple.z) / 2;
    const pitchAngle = (noseBridge.z - avgTempleZ) * 1.8;

    const eyeCenterForYaw = (leftEyeInner.x + rightEyeInner.x) / 2;
    const yawAngle = (eyeCenterForYaw - 0.5) * 0.4;

    this.model.rotation.set(pitchAngle, yawAngle + Math.PI, -rollAngle);
  }

  showValidationMessage(message) {
    if (this.validationMessageTimeout) {
      clearTimeout(this.validationMessageTimeout);
    }

    let msgElement = document.getElementById('vto-validation-message');

    if (!msgElement) {
      msgElement = document.createElement('div');
      msgElement.id = 'vto-validation-message';

      const pdHeight = document.getElementById('pd-container')?.offsetHeight || 320;

      msgElement.style.cssText = `
        position: fixed;
        top: ${this.isMobile ? (pdHeight + 10) + 'px' : '20px'};
        left: 50%;
        transform: translateX(-50%);
        background: rgba(255, 152, 0, 0.95);
        color: white;
        padding: ${this.isMobile ? '10px 20px' : '15px 30px'};
        border-radius: 8px;
        font-size: ${this.isMobile ? '14px' : '18px'};
        font-weight: bold;
        z-index: 9999;
        box-shadow: 0 4px 12px rgba(0,0,0,0.3);
        text-align: center;
        max-width: ${this.isMobile ? '90%' : '80%'};
        transition: opacity 0.3s;
      `;

      const overlay = document.getElementById('vto-overlay');
      if (overlay) {
        overlay.appendChild(msgElement);
      }
    }

    msgElement.textContent = message;
    msgElement.style.display = 'block';
    msgElement.style.opacity = '1';
  }

  clearValidationMessage() {
    const msgElement = document.getElementById('vto-validation-message');
    if (msgElement) {
      msgElement.style.opacity = '0';
      this.validationMessageTimeout = setTimeout(() => {
        msgElement.style.display = 'none';
      }, 300);
    }
  }

  animate() {
    if (!this.cameraInitialized || !this.webcamRunning) return;

    this.renderer.render(this.scene, this.camera);

    requestAnimationFrame(() => this.animate());
  }
}

// Initialize VTO when page loads
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.vtoSystem = new VirtualTryOnSystem();
    window.vtoSystem.init();
  });
} else {
  window.vtoSystem = new VirtualTryOnSystem();
  window.vtoSystem.init();
}

console.log('🎉 VTO Script Loaded (IMPROVED MOBILE + CONSENT GATE)');
